/**
 * Blocks rendering until the signed-in user's organization is known.
 *
 * WHY A GATE RATHER THAN PER-SCREEN HANDLING
 *
 * `currentOrganization` is a prerequisite for every org-scoped query, and those
 * queries are gated on it (`enabled: !!email && !!orgId`). A gated query never
 * runs, never errors, and never resolves — so a screen rendered without an org
 * shows its EMPTY state, which is indistinguishable from "you genuinely have no
 * work today".
 *
 * That is how a dispatched technician's PWA told him he had nothing scheduled
 * for days. The fix is not to teach every screen to check for an org before
 * trusting its own emptiness — that is a rule each new screen can forget. It is
 * to make the bad state unreachable: nothing org-scoped renders until the org
 * is known, so no screen has to remember.
 *
 * Wrap the routed content of an app whose screens are ALL org-scoped (the
 * technician PWA). Do not wrap surfaces that must work without an org, such as
 * sign-in or org creation.
 */
import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { CloudOff } from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Button } from '../forms/Button';
import { Card } from './Card';
import { ListSkeleton } from './Skeletons';

interface OrgBoundaryProps {
  /** Omit to use as a React Router layout route — renders an <Outlet /> instead. */
  children?: ReactNode;
  /** Rendered while the first load is still in flight. */
  fallback?: ReactNode;
}

export function OrgBoundary({ children, fallback }: OrgBoundaryProps) {
  const { currentOrganization, loading, error, retryLoad } = useOrganization();

  // An org already resolved is authoritative even if a later refresh failed —
  // a transient error must not blank a working app.
  if (currentOrganization) return <>{children ?? <Outlet />}</>;

  if (loading) {
    return (
      <>
        {fallback ?? (
          <div className="min-h-screen bg-slate-50 p-4">
            <ListSkeleton rows={4} />
          </div>
        )}
      </>
    );
  }

  // Not loading, no org. Either the load failed, or the account genuinely has
  // no active membership. Both are dead ends for an org-scoped app, and both
  // must SAY so rather than rendering empty screens — but they need different
  // words, because only one of them is worth retrying.
  const isLoadFailure = error !== null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <Card padding="lg" className="w-full max-w-sm text-center">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <CloudOff className="w-6 h-6 text-slate-500" aria-hidden="true" />
        </div>

        <h1 className="text-base font-black text-slate-900 mb-2">
          {isLoadFailure ? "Can't load your account" : 'No workspace assigned'}
        </h1>

        <p className="text-sm text-slate-600 mb-5">
          {isLoadFailure
            ? "We couldn't reach OfficeHub to confirm which team you're on, so your jobs can't be shown yet. This is usually signal — it retries on its own when you're back online."
            : 'Your login is not attached to a workspace yet, so there are no jobs to show. Ask the office to finish setting up your account.'}
        </p>

        {/*
          Retry on BOTH branches. "No workspace" is reachable transiently — a
          new hire whose membership record has not propagated yet reads exactly
          like a permanent misconfiguration, and without a retry their only
          recourse is to reinstall the app.
        */}
        <Button variant="primary" onClick={retryLoad} className="w-full">
          Try again
        </Button>
      </Card>
    </div>
  );
}

export default OrgBoundary;
